import type { TextAsset } from '@lumen5/luminary';

function escapeSpecialCharacters(text: string): string {
    return text
        .replace(/\\/g, '\\\\') // Escape backslashes first
        .replace(/\n/g, '\\n') // Escape newlines
        .replace(/\r/g, '\\r') // Escape carriage returns
        .replace(/\t/g, '\\t'); // Escape tabs
}

export function getTextAndHighlightsFromMarkdown(markdown: string): {
    text: string;
    highlights: [number, number][];
} {
    const highlights: [number, number][] = [];
    let text = '';
    let currentPosition = 0;

    // Find all **text** patterns
    const highlightRegex = /\*\*([^*]+)\*\*/g;
    let match;
    let lastIndex = 0;

    while ((match = highlightRegex.exec(markdown)) !== null) {
        const beforeHighlight = markdown.slice(lastIndex, match.index);
        const highlightedText = match[1];

        // Add text before highlight to clean text
        text += beforeHighlight;
        currentPosition += beforeHighlight.length;

        // Record highlight start and end positions
        const start = currentPosition;
        const end = currentPosition + highlightedText.length;
        highlights.push([start, end]);

        // Add highlighted text to clean text
        text += highlightedText;
        currentPosition += highlightedText.length;

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last highlight
    text += markdown.slice(lastIndex);

    // Ensure highlights are sorted by start position
    highlights.sort((a, b) => a[0] - b[0]);

    return { text, highlights };
}

export function getTextAndHighlightsAsMarkdown(asset: TextAsset): string {
    let markdown = asset.text;

    // Apply highlights from the end to keep the indices valid
    asset.textHighlights
        .sort((a, b) => b[1] - a[1])
        .forEach(([start, end]) => {
            // Slice the text into three parts: before, highlight, after
            const before = markdown.slice(0, start);
            const highlight = markdown.slice(start, end);
            const after = markdown.slice(end);

            // Apply markdown bold syntax to the highlight and reconstruct the string
            // we replace all tabs (\t), newlines (\n), and carriage returns (\r) from the start
            // and end of the highlight. We leave spaces to easily support subword highlights.
            markdown =
                before + '**' + highlight.replace(/^[\t\n\r]+|[\t\n\r]+$/g, '') + '**' + after;
        });

    // Escape special characters so they're visible to LLMs
    return escapeSpecialCharacters(markdown);
}
